// Change #100668 - "we need to see signin logs. date time, device (saved not
// saved) gps if possible. ip address, etc".
//
// One user's sign-ins INTO THIS COMPANY (usersigninlog/list/{User_Id}). Users
// span companies, so sign-ins to their other companies are never shown here,
// and recording only started with this change.
import React, { useEffect, useState } from "react";
import { Badge, Spinner, Table } from "reactstrap";
import HistoryIcon from "@mui/icons-material/History";
import AppModal from "../../../../Components/Common/AppModal";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import { HandleDateTimeAgo } from "../../../../Components/Common/HandleDateTimeAgo";
import { getUserSignInLogList } from "../../../../helpers/fakebackend_helper";

// "Chrome on Windows" from a user agent - enough to recognise a device.
const describeAgent = (ua) => {
  if (!ua) return "";
  if (/CRMitApp/i.test(ua)) return "CRMit app";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\//.test(ua)
    ? "Opera"
    : /Chrome\//.test(ua)
    ? "Chrome"
    : /Firefox\//.test(ua)
    ? "Firefox"
    : /Safari\//.test(ua)
    ? "Safari"
    : "";
  const os = /iPhone|iPad|iPod/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
    ? "Android"
    : /Windows/.test(ua)
    ? "Windows"
    : /Mac OS X|Macintosh/.test(ua)
    ? "macOS"
    : /Linux/.test(ua)
    ? "Linux"
    : "";
  return [browser, os].filter(Boolean).join(" on ") || "Unknown browser";
};

const SignInLogModal = ({ user, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trackingStarted, setTrackingStarted] = useState(true);

  useEffect(() => {
    if (!user?.User_Id) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      const res = await getUserSignInLogList(user.User_Id);
      if (cancelled) return;
      setLoading(false);
      if (res?.status === 200) {
        setRows(res.usersigninlog_list || []);
        setTrackingStarted(res.tracking_started !== false);
      } else {
        setError(res?.message || "Could not load the sign-in log");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.User_Id]);

  const name =
    user?.User_UserFullName ||
    [user?.User_UserFirstName, user?.User_UserLastName]
      .filter(Boolean)
      .join(" ") ||
    `User #${user?.User_Id}`;

  return (
    <AppModal
      open={!!user}
      onClose={onClose}
      title="Sign-in log"
      subtitle={name}
      icon={<HistoryIcon />}
      headerAccent="slate"
      size="lg"
      hideFooter
    >
      {loading ? (
        <div className="text-center text-muted py-4">
          <Spinner size="sm" className="me-2" />
          Loading sign-ins...
        </div>
      ) : error ? (
        <div className="alert alert-warning mb-0">{error}</div>
      ) : !trackingStarted ? (
        <div className="alert alert-info mb-0">
          Sign-in logging for this company has not started yet. It begins once
          the latest update has been applied.
        </div>
      ) : rows.length === 0 ? (
        <div className="text-muted py-3">
          No sign-ins to this company recorded yet. Logging started on 26 Oct
          2026; earlier sign-ins are not shown.
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>When</th>
                  <th>Device</th>
                  <th>IP address</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const saved = Number(row.Device_TrustedFlag) === 1;
                  const hasGps =
                    row.Latitude !== null &&
                    row.Latitude !== undefined &&
                    row.Longitude !== null &&
                    row.Longitude !== undefined;
                  return (
                    <tr key={row.Id}>
                      <td>
                        <div>{HandleDateTime(row.CreatedDateTime)}</div>
                        <div className="text-muted fs-11">
                          {HandleDateTimeAgo(row.CreatedDateTime)}
                        </div>
                      </td>
                      <td>
                        <div>
                          {row.Device_Name || describeAgent(row.UserAgent)}{" "}
                          {saved ? (
                            <Badge
                              color="success"
                              title="A device the user has named and saved"
                            >
                              Saved
                            </Badge>
                          ) : (
                            <Badge
                              color="warning"
                              className="text-dark"
                              title="Not a saved (named) device"
                            >
                              Not saved
                            </Badge>
                          )}
                        </div>
                        {row.Device_Name && (
                          <div className="text-muted fs-11">
                            {describeAgent(row.UserAgent)}
                          </div>
                        )}
                      </td>
                      <td className="font-monospace small">
                        {row.IpAddress || <span className="text-muted">—</span>}
                      </td>
                      <td>
                        {hasGps ? (
                          <a
                            href={`https://www.google.com/maps?q=${row.Latitude},${row.Longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="ri-map-pin-line align-middle me-1"></i>
                            Map
                          </a>
                        ) : (
                          <span
                            className="text-muted small"
                            title="Only recorded when the browser already allowed location"
                          >
                            Not shared
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <div className="small text-muted mt-2">
            Newest {rows.length} sign-ins to this company. Location is only
            recorded when the browser had already allowed it.
          </div>
        </>
      )}
    </AppModal>
  );
};

export default SignInLogModal;
